<template>
  <div class="notifier-wrapper">
    <transition-group name="notifier-slide" tag="div" class="notifier-container">
      <div v-for="t in toasts" :key="t.id" class="notifier-item" role="alert" aria-live="assertive">
        <div class="notifier-toast" :class="[`notifier-${t.type}`]">
          <!-- Icon Container -->
          <div class="notifier-icon-wrapper">
            <div class="notifier-icon">
              <v-icon :color="t.iconColor" size="24">{{ t.icon }}</v-icon>
            </div>
          </div>

          <!-- Content -->
          <div class="notifier-content-wrapper">
            <div class="notifier-title">{{ t.title }}</div>
            <div class="notifier-text">{{ t.text }}</div>
          </div>

          <!-- Close Button -->
          <button class="notifier-close" @click="close(t.id)" aria-label="Cerrar notificación">
            <v-icon size="18">mdi-close</v-icon>
          </button>

          <!-- Progress Bar -->
          <div v-if="t.timeout > 0" class="notifier-progress" :style="{ animationDuration: t.timeout + 'ms' }"></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
let nextToastId = 1

export default {
  name: 'NotifierComponent',
  data() {
    return {
      toasts: []
    }
  },
  methods: {
    notify({ text = '', type = 'success', duration = 3000, title = null } = {}) {
      const id = nextToastId++

      // Color and icon configuration for each type
      const typeConfig = {
        success: {
          bgColor: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderColor: '#059669',
          iconColor: '#ffffff',
          icon: 'mdi-check-circle'
        },
        error: {
          bgColor: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          borderColor: '#dc2626',
          iconColor: '#ffffff',
          icon: 'mdi-alert-circle'
        },
        warning: {
          bgColor: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderColor: '#d97706',
          iconColor: '#ffffff',
          icon: 'mdi-alert'
        },
        info: {
          bgColor: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderColor: '#2563eb',
          iconColor: '#ffffff',
          icon: 'mdi-information'
        }
      }

      const config = typeConfig[type] || typeConfig.info

      const toast = {
        id,
        text,
        title: title || this._getDefaultTitle(type),
        type,
        icon: config.icon,
        iconColor: config.iconColor,
        bgColor: config.bgColor,
        borderColor: config.borderColor,
        timeout: duration
      }

      this.toasts.unshift(toast)

      if (duration > 0) {
        setTimeout(() => this.close(id), duration)
      }

      return id
    },

    close(id) {
      const index = this.toasts.findIndex(t => t.id === id)
      if (index !== -1) {
        this.toasts.splice(index, 1)
      }
    },

    _getDefaultTitle(type) {
      const titles = {
        success: '¡Éxito!',
        error: 'Error',
        warning: 'Advertencia',
        info: 'Información'
      }
      return titles[type] || 'Notificación'
    }
  }
}
</script>

<style scoped>
/* Wrapper and Container */
.notifier-wrapper {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 9999;
  pointer-events: none;
  padding: 1.5rem;
}

.notifier-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-end;
}

/* Toast Item */
.notifier-item {
  pointer-events: auto;
}

.notifier-toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  min-width: 320px;
  max-width: 420px;
  border-radius: 12px;
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(255, 255, 255, 0.1);
  border: 1px solid;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%);
  color: #1f2937;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Type-specific styles */
.notifier-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.notifier-error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.notifier-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.notifier-info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

/* Icon Wrapper */
.notifier-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notifier-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(4px);
}

/* Content */
.notifier-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.notifier-title {
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.notifier-text {
  font-size: 0.875rem;
  font-weight: 400;
  opacity: 0.95;
  line-height: 1.4;
}

/* Close Button */
.notifier-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: currentColor;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.notifier-close:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: rotate(90deg);
}

.notifier-close:active {
  transform: rotate(90deg) scale(0.95);
}

/* Progress Bar */
.notifier-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.4);
  animation: notifier-progress-shrink linear forwards;
}

@keyframes notifier-progress-shrink {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Transitions */
.notifier-slide-enter-active,
.notifier-slide-leave-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.notifier-slide-enter-from {
  opacity: 0;
  transform: translateX(420px) translateY(-20px);
}

.notifier-slide-enter-to {
  opacity: 1;
  transform: translateX(0) translateY(0);
}

.notifier-slide-leave-from {
  opacity: 1;
  transform: translateX(0) translateY(0);
}

.notifier-slide-leave-to {
  opacity: 0;
  transform: translateX(420px) translateY(-20px);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .notifier-wrapper {
    padding: 1rem;
  }

  .notifier-toast {
    min-width: 280px;
    max-width: 100vw;
    max-width: calc(100vw - 2rem);
    padding: 0.875rem 1rem;
    gap: 0.75rem;
  }

  .notifier-title {
    font-size: 0.9rem;
  }

  .notifier-text {
    font-size: 0.8rem;
  }
}
</style>
